import { Call, Event, Task } from 'types/lead-management';

export enum TimelineItemType {
  TASK = 'task',
  EVENT = 'event',
  CALL = 'call',
  NOTE = 'note',
  ATTACHMENT = 'attachment'
}

export enum TimelineEntity {
  LEAD = 'lead',
  LEAD_ACTIVITY_CALL = 'lead_activity_call',
  LEAD_ACTIVITY_TASK = 'lead_activity_task',
  LEAD_ACTIVITY_EVENT = 'lead_activity_event',
  LEAD_NOTES = 'lead_notes',
  LEAD_ATTACHMENTS = 'lead_attachments',
  LEAD_TAGS_MAP = 'lead_tags_map',
  PAYMENT_ORDER = 'payment_order',
  GROUP_MEMBER = 'group_member',
  BRANCH_MEMBER = 'branch_member'
}

export enum TimelineAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  SYNC = 'SYNC',
  MARK_PAID = 'MARK_PAID',
  DISABLE = 'DISABLE',
  CLOSE = 'CLOSE',
  SEND_REMINDER = 'SEND_REMINDER',
  ADD_DISCOUNT = 'ADD_DISCOUNT',
  ENABLE_SPLIT_PAYMENT = 'ENABLE_SPLIT_PAYMENT'
}

export enum ModifiedDataKey {
  STATUS = 'status',
  SOURCE = 'source',
  ASSIGNEE = 'assignee',
  PRIORITY = 'priority',
  DEAL_VALUE = 'deal_value',
  CATEGORY = 'category',
  LOCATION = 'location',
  MOBILE_NUMBER = 'mobile_number',
  PHONE_COUNTRY_CODE = 'phone_country_code',
  PHONE_SECONDARY = 'phone_secondary',
  PHONE_SECONDARY_COUNTRY_CODE = 'phone_secondary_country_code',
  EMAIL = 'email',
  USERNAME = 'username',
  DESCRIPTION = 'description',
  NOTE = 'note',
  DOCUMENT = 'document',
  TAG = 'tag',
  NAME = 'name',
  DATE = 'date',
  TYPE = 'type',
  OUTCOME = 'outcome',
  REMARKS = 'remarks',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  STATE = 'state',
  CITY = 'city',
  PAYMENT_ORDER = 'payment_order',
  BRANCH_MEMBER = 'branch_member',
  GROUP_MEMBER = 'group_member',
  EXTERNAL_SOURCE = 'external_source'
}

export interface ModifiedData {
  key: ModifiedDataKey;
  previous_value?: string;
  current_value?: string;
  document?: Array<{
    document_id: string;
    file_name: string;
    deleted_at?: string;
  }>;
  metadata?: {
    ad_name?: string;
    business_name?: string;
    provider?: string;
  };
}

export interface TimelineItemAPI {
  entity: TimelineEntity;
  entity_name?: string;
  action_type: TimelineAction;
  updated_by: string;
  timestamp: string;
  modified_data: ModifiedData[];
  arguments?: Arguments | null;
}

export interface Arguments {
  currency_symbol?: string;
  total_amount?: string;
  amount?: string;
  closed_amount?: string;
  note?: string;
  payment_method?: string;
  discount_amount?: string;
}

export interface TimelineResponse {
  data: TimelineItemAPI[];
  pagination: {
    page: number;
    limit: number;
    total_rows: number;
    last_page: boolean;
  };
}

export interface ModifiedDataItem {
  key: ModifiedDataKey | string;
  current_value?: string;
  previous_value?: string;
  document?: Array<{ file_name: string; deleted_at?: string | null }>;
  metadata?: {
    ad_name?: string;
    business_name?: string;
    provider?: string;
  };
}

export interface FieldConfig {
  readonly key: ModifiedDataKey | string;
  readonly displayName: string;
}

export interface EntityConfig {
  fields: ModifiedDataKey[];
  createMessage: (keyData: ModifiedDataItem | undefined) => string;
  fieldFormatter: (field: string) => string;
}

export interface TimelineItem {
  id: string;
  type: 'task' | 'event' | 'call' | 'note' | 'attachment';
  timestamp: string;
  date: string;
  time: string;
  user: string;
  title: string;
  description?: string;
  details?: TimelineItemDetails;
}
export interface Outcomes {
  interested: string;
  notInterested: string;
  followUp: string;
  didNotPickUp: string;
  notAbleToConnect: string;
}
export enum TaskType {
  FOLLOW_UP = 'follow-up',
  DEMO_BOOKED = 'demo-booked',
  SETUP_DEMO = 'setup-demo'
}
export interface TimelineItemDetails {
  // For tasks
  taskName?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  taskType?: 'follow-up' | 'demo-booked' | 'setup-demo';
  taskStatus?: string;

  // For events
  eventName?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  callType?: 'inbound' | 'outbound';
  outcome?:
    | 'interested'
    | 'not-interested'
    | 'follow-up'
    | 'did-not-pick-up'
    | 'not-able-to-connect'
    | 'invalid-number';
  noteContent?: string;
  fileName?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: string;
}

export interface TimelineGroup {
  date: string;
  items: TimelineItem[];
}

export interface TimelineProps {
  items: TimelineItem[];
  className?: string;
  initialItemsToShow?: number;
  onLoadMore?: () => void;
  hasMoreItems?: boolean;
  isLoading?: boolean;
}

export interface TimelineTabProps {
  leadId: string;
}

export interface TimeLineErrorProps {
  handleRefetch: () => void;
}

export interface TimelineStepperProps {
  timelineData: TimelineItemAPI[];
  className?: string;
}

export type EntityType = 'call' | 'task' | 'event';

export interface FileAttachmentProps {
  modifiedData: Array<{
    key: string;
    document?: Array<{
      document_id: string;
      file_name: string;
      deleted_at?: string;
    }>;
  }>;
  actionType: TimelineAction;
  entity: TimelineEntity;
}

export interface TimelineItemProps {
  item: TimelineItemAPI;
  isLast: boolean;
}

export interface TimelineGroupProps {
  date: string;
  items: TimelineItemAPI[];
  groupIndex: number;
  totalGroups: number;
}

export enum CallType {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound'
}

export enum CallOutcome {
  INTERESTED = 'interested',
  NOT_INTERESTED = 'not_interested',
  FOLLOW_UP = 'follow_up',
  DID_NOT_PICK = 'did_not_pick',
  NOT_ABLE_TO_CONNECT = 'not_able_to_connect',
  SWITCH_OFF = 'switch_off',
  DISQUALIFIED = 'disqualified',
  INVALID_NUMBER = 'invalid_number'
}
export interface CreateCallResponse {
  id?: string;
  call_type: CallType;
  timestamp: string;
  outcome: CallOutcome;
  remarks?: string | null;
}

export interface CallResponse {
  data: CreateCallResponse[];
  pagination: {
    page: number;
    limit: number;
    total_rows: number;
    last_page: boolean;
  };
}

export interface CallFormData {
  id?: string;
  call_type: CallType;
  date: string;
  time: string;
  outcome: CallOutcome;
  remarks: string;
  timestamp?: string;
}

export interface CallModalProps extends BaseModalProps {
  onSubmit: () => void;
  initialData?: Partial<ActivityFormData>;
  leadId: string;
  analyticsData?: AnalyticsDataType;
}

export type ActivityType = 'task' | 'event' | 'call';

export type TaskTypeValue =
  | 'follow_up'
  | 'demo_booked'
  | 'setup_demo'
  | 'send_proposal';
export type TaskStatusValue = 'scheduled' | 'completed' | 'overdue';
export type OutcomeValue =
  | 'interested'
  | 'not_interested'
  | 'follow_up'
  | 'did_not_pick'
  | 'not_able_to_connect'
  | 'switch_off'
  | 'disqualified'
  | 'invalid_number';
export interface ActivityFormData {
  id?: string;
  name: string;
  timestamp: string;
  remarks: string;
  taskType?: TaskTypeValue;
  taskStatus?: TaskStatusValue;
  startTime?: string;
  endTime?: string;
  location?: string;
  call_type?: CallType;
  callType?: CallType;
  task_type?: TaskTypeValue;
  status?: TaskStatusValue;
  outcome?: CallOutcome;
  date?: string;
  time?: string;
  end_time?: string;
  start_time?: string;
}

export interface BaseModalProps {
  onClose: () => void;
  onSubmit: () => void;
  initialData?: Partial<any>;
}

export interface TaskFormData {
  id?: string;
  name: string;
  date: string;
  time: string;
  taskType: TaskTypeValue;
  taskStatus: TaskStatusValue;
  timestamp?: string;
  remarks: string;
}

export interface SubmitTaskData {
  id?: string;
  name: string;
  timestamp: string;
  task_type: TaskTypeValue;
  status: TaskStatusValue;
  remarks: string | null;
}

export interface TaskModalProps extends BaseModalProps {
  onSubmit: () => void;
  initialData?: Partial<TaskFormData>;
  leadId: string;
  analyticsData?: AnalyticsDataType;
}

export interface EventFormData {
  id?: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  remarks: string;
}

export interface EventModalProps extends BaseModalProps {
  onSubmit: () => void;
  initialData?: Partial<EventFormData>;
  leadId: string;
  analyticsData?: AnalyticsDataType;
}

export interface SubmitEventData {
  id?: string;
  name: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  remarks: string;
}

export interface SubmitCallData {
  id?: string;
  callType: CallType;
  timestamp: string;
  outcome: CallOutcome;
  remarks: string;
}

export interface AnalyticsDataType {
  leadDetails: { leadName: string; leadPhone: string };
}

export interface ActivityModalProps {
  onClose: () => void;
  type: ActivityType;
  onSubmit: () => void;
  initialData?: Partial<ActivityFormData>;
  leadId: string;
  analyticsData?: AnalyticsDataType;
}

export interface Tab {
  id: string;
  label: string;
  isActive: boolean;
}

export interface DeleteModalProps {
  id: string;
  type: ActivityType;
  name?: string;
  isLastItem?: boolean;
}

export interface LeadActivityPanelProps {
  leadId: string;
  className?: string;
  analyticsData?: AnalyticsDataType;
}

export interface CallTableProps {
  calls: Call[];
  page: number;
  totalCount: number;
  setPage: (page: number) => void;
  handleMenuIconClick: (callId: string, action: 'edit' | 'delete') => void;
}

export interface EventTableProps {
  events: Event[];
  page: number;
  totalCount: number;
  setPage: (page: number) => void;
  handleMenuIconClick: (eventId: string, action: 'edit' | 'delete') => void;
}

export interface TaskTableProps {
  tasks: Task[];
  page: number;
  totalCount: number;
  setPage: (page: number) => void;
  handleMenuIconClick: (taskId: string, action: 'edit' | 'delete') => void;
}
